import { ReactIcon } from '@/components/react/ui/ReactIcon';
import { type IconName } from '@/consts';
import { cva, cx, type VariantProps } from '@/cva.config';
import { type HTMLAttributes } from 'react';

export const linkVariants = cva({
  base: [
    'inline-flex',
    'justify-center',
    'text-base',
    'lg:text-normal',
    'font-sans',
    'whitespace-nowrap',
    'cursor-pointer',
    'transition-all',
    'duration-150',
    'ease-in-out',
  ],
  variants: {
    disabled: {
      false: null,
      true: 'pointer-events-none',
    },
    intent: {
      breadcrumb: [
        'no-underline',
        'font-normal',
        'items-start',
        'justify-start',
        'text-blurb-text-link',
        // Hover
        'hover:text-blurb-text-link-hover',
        // Active
        'active:text-blurb-text-link-hover',
      ],
      editorial: 'py-2 items-center rounded-sm no-underline',
      label: [
        'gap-1',
        'items-start',
        'text-blurb-text-link',
        'underline',
        'underline-offset-2',
        // Hover
        'hover:text-blurb-text-link-hover',
        // Active
        'active:text-blurb-text-link-hover',
      ],
      promo: 'py-2 items-center rounded-sm no-underline',
      text: [
        'items-start',
        'text-blurb-text-bold',
        'no-underline',
        'border-b',
        'border-blurb-border-bold',
        // Hover
        'hover:link-shadow-blurb-border-bold',
        // Active
        'active:link-shadow-blurb-border-bold',
      ],
      unset: null,
    },
    size: {
      large: 'gap-2 px-6 min-w-25 text-base/6',
      small: 'gap-1 px-4 min-w-17.5 text-sm/5',
      unset: null,
    },
    fullWidth: {
      false: 'max-w-min',
      mobileAndTabletOnly: 'w-full max-w-full lg:max-w-min',
      mobileOnly: 'w-full max-w-full md:max-w-min',
      true: 'w-full max-w-full',
    },
    variant: {
      icon: [
        'items-center',
        'text-blurb-text-link',
        'border-none',
        // Hover
        'hover:text-blurb-text-link-hover',
        'hover:link-shadow-unset',
        // Active
        'active:text-blurb-text-link-active',
        'active:link-shadow-unset',
      ],
      filled: 'text-blurb-text-inverse',
      subtle: 'border bg-blurb-bg-neutral',
      unset: null,
      semibold: 'font-semibold',
    },
  },
  compoundVariants: [
    // Promo - Filled
    {
      intent: 'promo',
      variant: 'filled',
      class: [
        'bg-blurb-bg-brand',
        // Hover
        'hover:bg-blurb-bg-brand-hover',
        'hover:button-shadow-light-blue-800',
        // Active
        'active:bg-blurb-bg-brand-active',
        'active:button-shadow-light-blue-950',
      ],
    },
    // Promo - Subtle
    {
      intent: 'promo',
      variant: 'subtle',
      class: [
        'bg-blurb-bg-neutral',
        'text-blurb-text-brand',
        'border-blurb-border-brand',
        // Hover
        'hover:text-blurb-text-link-hover',
        'hover:bg-blurb-bg-neutral-hover',
        'hover:border-light-blue-800',
        'hover:button-shadow-light-blue-800',
        // Active
        'active:text-blurb-text-link-active',
        'active:bg-blurb-bg-neutral-active',
        'active:border-light-blue-950',
        'active:button-shadow-light-blue-950',
      ],
    },
    // Editorial - Filled
    {
      intent: 'editorial',
      variant: 'filled',
      class: [
        'bg-blurb-bg-gray-bold',
        // Hover
        'hover:bg-blurb-bg-gray-bold-hover',
        'hover:button-shadow-light-gray-800',
        // Active
        'active:bg-blurb-bg-gray-bold-active',
        'active:button-shadow-light-gray-600',
      ],
    },
    // Editorial - Subtle
    {
      intent: 'editorial',
      variant: 'subtle',
      class: [
        'text-blurb-text-bold',
        'bg-blurb-bg-neutral',
        'border-blurb-border-bold',
        // Hover
        'hover:bg-blurb-bg-neutral-hover',
        'hover:border-light-gray-800',
        'hover:button-shadow-light-gray-800',
        // Active
        'active:bg-blurb-bg-neutral-active',
        'active:border-light-gray-600',
        'active:button-shadow-light-gray-600',
      ],
    },
    // Editorial & Promo - Disabled
    {
      intent: ['editorial', 'promo'],
      disabled: true,
      class:
        'text-blurb-text-disabled bg-blurb-bg-neutral-disabled border-none',
    },
    // Text & Label - Base
    {
      intent: ['text', 'label'],
      class: 'px-0 min-w-0',
    },
    // Text & Label - Disabled
    {
      intent: ['text', 'label'],
      disabled: true,
      class: 'text-blurb-text-disabled border-blurb-border-subtle',
    },
    //  Label - Large
    {
      intent: 'label',
      size: 'large',
      class: 'gap-1',
    },
    // Breadcrumb - Small
    {
      intent: 'breadcrumb',
      size: 'small',
      class: 'px-0 min-w-0',
    },
  ],
});

type CtaProps = ButtonCtaProps | AnchorCtaProps;

interface ButtonCtaProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, 'type'>,
    VariantProps<typeof linkVariants> {
  disabled?: boolean;
  href: never;
  icon?: IconName;
  text?: string;
  type?: 'submit' | 'reset' | 'button';
}

interface AnchorCtaProps
  extends Omit<HTMLAttributes<HTMLAnchorElement>, 'type'>,
    VariantProps<typeof linkVariants> {
  disabled?: boolean;
  href?: string;
  icon?: IconName;
  text?: string;
  type?: 'submit' | 'reset' | 'button';
}

export const ReactCta = ({
  children,
  className,
  disabled = false,
  fullWidth = false,
  icon,
  intent = 'unset',
  size = 'large',
  text,
  variant,
  ...props
}: CtaProps) => {
  const content = (
    <>
      {icon && <ReactIcon name={icon} size="small" />}
      {children || text}
      {variant === 'icon'
        && (intent === 'text' || intent === 'label')
        && !icon && (
          <ReactIcon
            name={intent === 'text' ? 'arrowRight' : 'openInNew'}
            size={size === 'small' ? 'small' : 'large'}
          />
        )}
    </>
  );

  return props.href ?
      <a
        className={cx(
          linkVariants({ disabled, intent, size, variant, fullWidth }),
          className,
        )}
        disabled={disabled}
        {...(props as AnchorCtaProps)}
      >
        {content}
      </a>
    : <button
        className={cx(
          linkVariants({ disabled, intent, size, variant, fullWidth }),
          className,
        )}
        disabled={disabled}
        {...(props as ButtonCtaProps)}
      >
        {content}
      </button>;
};
